'use client';

import type { ProductInformation } from '@dealerportal/common';
import { useState } from 'react';
import { Viewer3D } from '../../../../../ui-components/src/lib/Viewer3D';
import type { ItemModel } from '../Configurator/useItemModel';
import { MarkFavourite } from '../ProductCard/MarkFavourite';
import { Gallery } from './Gallery';
import { useFavouriteToggle } from './useFavouriteToggle';

export type KbfProductMediaProps = {
  productInfo: ProductInformation;
  /**
   * Image of the configuration as picked — the selected door style's render.
   * Takes the hero over the catalog's own item image (for cabinets, usually a
   * line drawing).
   */
  heroImage?: string;
  /** The current configuration's 3D model, from `useItemModel`. */
  model?: ItemModel;
};

/**
 * Hero media for a KB&F item: the picked door's render (or the catalog
 * image) in the same gallery flooring uses, favourites heart included. The
 * pages' configurator already shows the 3D model, so the model only stands
 * in here when there is no image at all.
 */
export const KbfProductMedia = ({
  productInfo,
  heroImage,
  model,
}: KbfProductMediaProps) => {
  const { isFavourite, toggleFavourite } = useFavouriteToggle(productInfo);
  // Remember which model failed to display, so the next configuration gets
  // its chance while this one falls back to the image.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const viewerFailed = failedSrc != null && failedSrc === model?.src;

  const catalogImage = productInfo.swatches.find(
    (swatch) => swatch.imageURL
  )?.imageURL;
  const image = heroImage || catalogImage;

  const name = productInfo.productName || productInfo.colorName;
  const title = productInfo.brandName
    ? `${name} by ${productInfo.brandName}`
    : name;

  const showModel = Boolean(model?.src) && !viewerFailed && !image;

  if (!showModel) {
    // Cabinet imagery is a product render, not a tiling texture: show it
    // whole instead of cropping it to the box like a flooring swatch.
    const galleryInfo: ProductInformation =
      image && image !== catalogImage
        ? {
            ...productInfo,
            swatches: productInfo.swatches.length
              ? productInfo.swatches.map((swatch, index) =>
                  index === 0 ? { ...swatch, imageURL: image } : swatch
                )
              : [
                  {
                    colorName: name,
                    imageURL: image,
                    slug: productInfo.slug,
                    sku: productInfo.sku,
                    UPID: productInfo.UPID,
                  },
                ],
            UPID: productInfo.swatches[0]?.UPID ?? productInfo.UPID,
          }
        : productInfo;
    return (
      // The gallery keeps the image it opened on as state; remount it when a
      // different door style is picked so the hero follows.
      <Gallery
        key={image ?? 'missing'}
        productInfo={galleryInfo}
        setSpecs={() => undefined}
        fit="contain"
      />
    );
  }

  return (
    <div className="ui-relative">
      <Viewer3D
        src={model?.src ?? ''}
        poster={image}
        alt={`3D model of ${title}`}
        className="ui-aspect-square ui-w-full ui-rounded-md ui-bg-white"
        onError={() => setFailedSrc(model?.src ?? null)}
      />
      <MarkFavourite
        btnClass="!ui-absolute !ui-top-[2.5%] !ui-right-[2.5%] ui-size-[2.5rem]"
        iconClass="ui-size-[1.35rem]"
        isFavourite={isFavourite}
        onClick={toggleFavourite}
      />
    </div>
  );
};
